/**
 * ContainerDetailsPanel - Panel de detalles de contenedor (versión local)
 *
 * @version 1.0
 * @updated 2026-02-22
 * @module core/map-tools
 *
 * @description
 * Panel simplificado para gestión de contenedores de residuos.
 * Trabaja con el tipo Container del MapToolsContext (sin backend).
 *
 * @remarks
 * Esta es una versión local que NO requiere backend.
 * Cuando el módulo waste tenga backend completo, usar ContainerDetailsPanel
 * del módulo waste que tiene integración completa con la API.
 */

import { useState, useEffect, useMemo } from 'react';
import { Copy, Check, Trash2 } from 'lucide-react';
import type { Container } from '../MapToolsContext';
import { useRightPanel, useUnsavedChanges } from '../../layouts/panels/RightPanel';
import { ConfirmDialog } from '../../components';
import './container-details-panel.css';

/**
 * Tipo de pestañas disponibles
 */
type TabId = 'general' | 'status' | 'location';

/**
 * Props del componente
 */
export interface ContainerDetailsPanelProps {
  /**
   * Datos del contenedor a mostrar/editar
   */
  container: Container;

  /**
   * Callback cuando se guarda el contenedor
   */
  onSave: (updatedContainer: Container) => void;

  /**
   * Callback cuando se cancela la edición
   */
  onCancel: () => void;

  /**
   * Callback cuando se elimina el contenedor
   */
  onDelete: (containerId: string) => void;

  /**
   * Si true, el panel es de solo lectura
   */
  readOnly?: boolean;
}

/**
 * Opciones de tipo de contenedor
 */
const CONTAINER_TYPE_OPTIONS: { value: Container['containerType']; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'recyclable', label: 'Reciclable' },
  { value: 'organic', label: 'Orgánico' },
  { value: 'glass', label: 'Vidrio' },
  { value: 'hazardous', label: 'Peligroso' },
];

/**
 * Opciones de estado
 */
const CONTAINER_STATUS_OPTIONS: { value: Container['status']; label: string }[] = [
  { value: 'active', label: 'Activo' },
  { value: 'full', label: 'Lleno' },
  { value: 'damaged', label: 'Dañado' },
  { value: 'removed', label: 'Removido' },
];

/**
 * Componente ContainerDetailsPanel (versión local)
 */
export function ContainerDetailsPanel({
  container,
  onSave,
  onCancel,
  onDelete,
  readOnly = false,
}: ContainerDetailsPanelProps): JSX.Element {
  const { registerUnsavedChangesCheck } = useRightPanel();
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [formData, setFormData] = useState<Container>(container);
  const [originalData, setOriginalData] = useState<Container>(container); // Estado separado para el original
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  /**
   * Detecta si es un contenedor nuevo (no persistido)
   */
  const isNewContainer = useMemo(() => {
    return container.id.startsWith('container-');
  }, [container.id]);

  // Hook para detectar cambios sin guardar
  const { hasUnsavedChanges, confirmDiscard } = useUnsavedChanges(
    originalData, // Estado original (sincronizado con formData)
    formData,
    {
      confirmMessage: isNewContainer
        ? '¿Descartar contenedor sin guardar?\n\nEl contenedor desaparecerá del mapa.'
        : '¿Descartar cambios del contenedor?\n\nLos cambios se perderán.',
    }
  );

  /**
   * Registrar función de verificación de cambios sin guardar
   */
  useEffect(() => {
    registerUnsavedChangesCheck(confirmDiscard);
    return () => {
      registerUnsavedChangesCheck(null);
    };
  }, [confirmDiscard, registerUnsavedChangesCheck]);

  /**
   * Resetear form cuando cambia el contenedor
   *
   * @remarks
   * IMPORTANTE: Resetea tanto formData como originalData para evitar
   * falsos positivos de "cambios sin guardar" al cambiar de contenedor.
   */
  useEffect(() => {
    setFormData(container);
    setOriginalData(container); // También resetear el original
  }, [container]);

  /**
   * Actualiza los datos del formulario
   */
  const handleFieldChange = <K extends keyof Container>(
    field: K,
    value: Container[K]
  ): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Copia las coordenadas al portapapeles
   */
  const handleCopyCoordinates = async (): Promise<void> => {
    const coordinatesText = `${formData.coordinates[0]}, ${formData.coordinates[1]}`;
    try {
      await navigator.clipboard.writeText(coordinatesText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar coordenadas:', err);
    }
  };

  /**
   * Guarda los cambios
   */
  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    try {
      const updatedContainer: Container = {
        ...formData,
        updatedAt: new Date(),
      };
      onSave(updatedContainer);
      // Marcar como guardado actualizando el original
      setOriginalData(updatedContainer);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Cancela la edición con verificación de cambios
   */
  const handleCancelOrDiscard = async (): Promise<void> => {
    if (hasUnsavedChanges && !await confirmDiscard()) {
      return;
    }
    setFormData(container);
    onCancel();
  };

  /**
   * Elimina el contenedor con confirmación
   */
  const handleDelete = async (): Promise<void> => {
    if (hasUnsavedChanges) {
      const discardConfirmed = await confirmDiscard();
      if (!discardConfirmed) return;
    }

    const confirmed = await ConfirmDialog.show({
      title: '¿Eliminar contenedor?',
      message: 'Esta acción no se puede deshacer.',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      type: 'danger',
    });

    if (confirmed) {
      onDelete(container.id);
    }
  };

  return (
    <div className="container-details-panel-local">
      {/* Header */}
      <div className="container-header-local">
        <Trash2 size={20} className="container-icon" />
        <div className="container-header-text">
          <span className="container-code">{formData.code}</span>
          <span className="container-type-label">
            {CONTAINER_TYPE_OPTIONS.find(t => t.value === formData.containerType)?.label || 'Contenedor'}
          </span>
        </div>
      </div>

      {/* Pestañas */}
      <div className="container-tabs-local" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'general'}
          className={`container-tab-local ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'status'}
          className={`container-tab-local ${activeTab === 'status' ? 'active' : ''}`}
          onClick={() => setActiveTab('status')}
        >
          Estado
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'location'}
          className={`container-tab-local ${activeTab === 'location' ? 'active' : ''}`}
          onClick={() => setActiveTab('location')}
        >
          Ubicación
        </button>
      </div>

      {/* Contenido de pestañas */}
      <div className="container-content-local">
        <div style={readOnly ? { pointerEvents: 'none', opacity: 0.65 } : undefined}>
          {/* Tab: General */}
          {activeTab === 'general' && (
            <div className="container-panel-local">
              <div className="form-group-local">
                <label className="form-label-local">Código</label>
                <input
                  type="text"
                  className="form-input-local"
                  value={formData.code}
                  disabled
                />
              </div>

              <div className="form-group-local">
                <label className="form-label-local">Tipo de contenedor</label>
                <select
                  className="form-select-local"
                  value={formData.containerType}
                  onChange={(e) => handleFieldChange('containerType', e.target.value as Container['containerType'])}
                >
                  {CONTAINER_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group-local">
                <label className="form-label-local">Capacidad (litros)</label>
                <input
                  type="number"
                  className="form-input-local"
                  value={formData.capacity}
                  onChange={(e) => handleFieldChange('capacity', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </div>
            </div>
          )}

          {/* Tab: Estado */}
          {activeTab === 'status' && (
            <div className="container-panel-local">
              <div className="form-group-local">
                <label className="form-label-local">Estado</label>
                <select
                  className="form-select-local"
                  value={formData.status}
                  onChange={(e) => handleFieldChange('status', e.target.value as Container['status'])}
                >
                  {CONTAINER_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group-local">
                <label className="form-label-local">Nivel de llenado</label>
                <div className="fill-indicator-local">
                  <div
                    className="fill-bar-local"
                    style={{ width: `${formData.currentFill ?? 0}%` }}
                  />
                  <span className="fill-value-local">{formData.currentFill ?? 0}%</span>
                </div>
                <input
                  type="range"
                  className="fill-slider-local"
                  value={formData.currentFill ?? 0}
                  onChange={(e) => handleFieldChange('currentFill', parseInt(e.target.value))}
                  min="0"
                  max="100"
                />
              </div>
            </div>
          )}

          {/* Tab: Ubicación */}
          {activeTab === 'location' && (
            <div className="container-panel-local">
              <div className="form-row-local">
                <div className="form-group-local">
                  <label className="form-label-local">Longitud</label>
                  <input
                    type="text"
                    className="form-input-local"
                    value={formData.coordinates[0].toFixed(6)}
                    disabled
                  />
                </div>
                <div className="form-group-local">
                  <label className="form-label-local">Latitud</label>
                  <input
                    type="text"
                    className="form-input-local"
                    value={formData.coordinates[1].toFixed(6)}
                    disabled
                  />
                </div>
                <button
                  type="button"
                  className="copy-btn-local"
                  onClick={handleCopyCoordinates}
                  title={copied ? 'Copiado!' : 'Copiar coordenadas'}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>

              <div className="form-group-local">
                <label className="form-label-local">Dirección</label>
                <input
                  type="text"
                  className="form-input-local"
                  value={formData.address ?? ''}
                  onChange={(e) => handleFieldChange('address', e.target.value)}
                  placeholder="Ej: Av. Principal 1234"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Botones de acción */}
      <div className="container-actions-local">
        {readOnly ? (
          <button className="btn-local btn-secondary-local" onClick={onCancel}>
            Cerrar
          </button>
        ) : (
          <>
            <button
              className="btn-local btn-primary-local"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Guardando...' : 'Guardar'}
            </button>

            <button
              className={isNewContainer ? 'btn-local btn-danger-local' : 'btn-local btn-secondary-local'}
              onClick={handleCancelOrDiscard}
              disabled={isSaving}
            >
              {isNewContainer ? 'Descartar' : 'Cancelar'}
            </button>

            {!isNewContainer && (
              <button
                className="btn-local btn-danger-local"
                onClick={handleDelete}
                disabled={isSaving}
              >
                Eliminar
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
